import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Fade,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as BotIcon,
  SkipNext as SkipIcon,
  Search as SearchIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  CropOriginal as TemplatesIcon,
  CreateNewFolder as FolderIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  SortByAlpha as SortIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Folder as FolderOpenIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  CheckBox as SelectAllIcon,
  AutoFixHigh as StudioIcon,
} from "@mui/icons-material";
import { alpha, styled, keyframes } from "@mui/material/styles";

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulseRing = keyframes`
  0%   { transform: scale(0.8); opacity: 0.8; }
  50%  { transform: scale(1.2); opacity: 0.4; }
  100% { transform: scale(0.8); opacity: 0.8; }
`;

const floatBot = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const typingDot = keyframes`
  0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
  40%            { transform: scale(1);   opacity: 1;   }
`;

const slideInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Theme ────────────────────────────────────────────────────────────────────

const PRIMARY   = "#3f51b5";
const SECONDARY = "#ff6b6b";
const GRADIENT  = `linear-gradient(135deg, ${PRIMARY} 0%, #7c4dff 100%)`;

// ─── Styled Components ────────────────────────────────────────────────────────

const BotAvatarStyled = styled(Avatar)(() => ({
  width: 56,
  height: 56,
  background: GRADIENT,
  boxShadow: `0 4px 20px ${PRIMARY}80`,
  animation: `${floatBot} 3s ease-in-out infinite`,
  cursor: "pointer",
  border: "3px solid white",
  transition: "transform 0.2s",
  "&:hover": { transform: "scale(1.1) translateY(-4px)" },
}));

const SpotlightOverlay = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 1200,
  pointerEvents: "none",
});

const BotCard = styled(Paper)({
  position: "fixed",
  zIndex: 1400,
  borderRadius: 20,
  overflow: "hidden",
  boxShadow: `0 24px 60px rgba(0,0,0,0.25), 0 8px 20px ${PRIMARY}40`,
  minWidth: 320,
  maxWidth: 395,
  animation: `${slideInUp} 0.4s ease`,
  border: "1px solid rgba(255,255,255,0.2)",
  backdropFilter: "blur(12px)",
});

const ProgressBar = styled(LinearProgress)(() => ({
  height: 4,
  borderRadius: 0,
  "& .MuiLinearProgress-bar": { background: GRADIENT },
  backgroundColor: `${PRIMARY}20`,
}));

const NextButton = styled(Button)(() => ({
  borderRadius: 50,
  textTransform: "none",
  fontWeight: 700,
  background: GRADIENT,
  color: "white",
  padding: "8px 24px",
  "&:hover": {
    opacity: 0.9,
    transform: "translateY(-2px)",
    boxShadow: `0 8px 20px ${PRIMARY}60`,
  },
  transition: "all 0.2s",
}));

const PulseRingBox = styled(Box)(() => ({
  position: "absolute",
  width: 60,
  height: 60,
  borderRadius: "50%",
  border: `3px solid ${PRIMARY}`,
  animation: `${pulseRing} 1.5s ease-out infinite`,
  pointerEvents: "none",
}));

// ─── Typing Dots ──────────────────────────────────────────────────────────────

const TypingIndicator = () => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8, height: 8, borderRadius: "50%",
          background: GRADIENT,
          animation: `${typingDot} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.16}s`,
        }}
      />
    ))}
  </Box>
);

// ─── Tour Steps ───────────────────────────────────────────────────────────────

const TOUR_STEPS = [
  {
    id: "welcome",
    targetId: null,
    title: "✨ Welcome to Synthium Studio!",
    description:
      "Hi there! I'm your Synthium Guide Bot. Let me walk you through all the powerful document management and creation features in this studio. It'll only take a minute!",
    color: PRIMARY,
    position: "center",
    highlight: null,
  },
  {
    id: "search",
    targetId: "coauthor-tour-search",
    title: "🔍 Search Documents",
    description:
      "Use the search bar to instantly filter through your documents, folders, shared files, and deleted items all at once. Results update in real-time as you type!",
    color: "#4CAF50",
    position: "bottom",
    highlight: "Real-time search across all docs",
  },
  {
    id: "new-project",
    targetId: "coauthor-tour-new-project",
    title: "➕ New Project",
    description:
      "Click 'New Project' to start writing a brand-new document from scratch in the CoAuthor editor. Your AI-powered writing assistant will be ready to help you craft amazing content!",
    color: PRIMARY,
    position: "bottom",
    highlight: "Start a new AI-assisted document",
  },
  {
    id: "upload-doc",
    targetId: "coauthor-tour-upload",
    title: "☁️ Upload Document",
    description:
      "Already have a document? Upload .txt, .doc, .docx, or .pdf files directly into the studio. The file is converted to HTML and opened in the editor automatically!",
    color: "#00BCD4",
    position: "bottom",
    highlight: "Import .txt, .doc, .docx, .pdf files",
  },
  {
    id: "templates",
    targetId: "coauthor-tour-templates",
    title: "📄 Use Templates",
    description:
      "Browse ready-made templates including Resume, Essay, Blog, Business Letter, Meeting Notes, Article, Cold Email, and Appendix. Filter by Academic, Professional, Personal, and Email & Letters categories!",
    color: "#9C27B0",
    position: "bottom",
    highlight: "8 professional templates available",
  },
  {
    id: "create-folder",
    targetId: "coauthor-tour-create-folder",
    title: "📁 Create New Folder",
    description:
      "Organize your documents into folders by clicking the folder icon. Give each folder a name (up to 25 characters) to keep your projects neatly categorized.",
    color: "#FF9800",
    position: "bottom",
    highlight: "Keep your docs organized",
  },
  {
    id: "view-toggle",
    targetId: "coauthor-tour-view-toggle",
    title: "⊞ Grid / List View",
    description:
      "Toggle between Grid View (card layout) and List View (compact rows) to display your documents the way you prefer. Grid is great for visual browsing; List is better for quick scanning.",
    color: "#795548",
    position: "bottom",
    highlight: "Switch between grid and list layout",
  },
  {
    id: "sort",
    targetId: "coauthor-tour-sort",
    title: "🔤 Sort Alphabetically",
    description:
      "Click the sort icon to arrange all your documents, folders, shared, and deleted items alphabetically. Click again to reverse the order from A→Z to Z→A!",
    color: "#607D8B",
    position: "bottom",
    highlight: "Sort A→Z or Z→A instantly",
  },
  {
    id: "download",
    targetId: "coauthor-tour-download",
    title: "⬇️ Download Documents",
    description:
      "Select one or more documents using the checkboxes, then click Download to export them. Choose between MS-Word (.docx) format or PDF — perfect for sharing outside the platform!",
    color: "#4CAF50",
    position: "bottom",
    highlight: "Export as .docx or PDF",
  },
  {
    id: "delete-toolbar",
    targetId: "coauthor-tour-delete-toolbar",
    title: "🗑️ Delete Selected Docs",
    description:
      "Select documents with their checkboxes, then click the delete icon in the toolbar to move them to the Deleted Documents section. Documents are soft-deleted and can be recovered!",
    color: "#f44336",
    position: "bottom",
    highlight: "Move selected docs to trash",
  },
  {
    id: "my-docs-section",
    targetId: "coauthor-tour-my-docs",
    title: "📂 My Documents",
    description:
      "All your personal documents and folders appear here as cards or list rows. The count shows how many items you have. Click any document card to open and continue editing it.",
    color: PRIMARY,
    position: "top",
    highlight: "Your personal document library",
  },
  {
    id: "select-all",
    targetId: "coauthor-tour-select-all",
    title: "☑️ Select All",
    description:
      "Use the 'Select All' checkbox to instantly select every document on the current page. Then use Download or Delete to perform bulk actions on all selected documents at once!",
    color: "#009688",
    position: "bottom",
    highlight: "Bulk select for batch actions",
  },
  {
    id: "refresh",
    targetId: "coauthor-tour-refresh",
    title: "🔄 Refresh Documents",
    description:
      "Click the Refresh button to reload all your documents from the server. Useful when you've been collaborating and want to see the latest changes made by others.",
    color: "#3F51B5",
    position: "bottom",
    highlight: "Sync latest changes from server",
  },
  {
    id: "doc-card",
    targetId: "coauthor-tour-doc-card",
    title: "📝 Document Card",
    description:
      "Each card shows the document name and a content preview. Hover to reveal action buttons: open, share, move to folder, or delete. Click anywhere on the card to open and edit it!",
    color: "#2196F3",
    position: "top",
    highlight: "Click to open · Hover for actions",
  },
  {
    id: "shared-section",
    targetId: "coauthor-tour-shared",
    title: "🤝 Shared With Me",
    description:
      "Documents that teammates have shared with you appear in this section. You can view and edit them based on the permissions granted. Your changes are synced in real-time!",
    color: "#00BCD4",
    position: "top",
    highlight: "Collaborative documents from others",
  },
  {
    id: "deleted-section",
    targetId: "coauthor-tour-deleted",
    title: "🗑️ Deleted Documents",
    description:
      "Soft-deleted documents live here. Select any document and use 'Delete Permanently' to remove it forever, or restore it from the document's action menu. Permanent deletion requires admin access.",
    color: "#f44336",
    position: "top",
    highlight: "Recover or permanently remove docs",
  },
  {
    id: "pagination",
    targetId: "coauthor-tour-pagination",
    title: "📄 Pagination",
    description:
      "If you have many documents, they are split across multiple pages. Use the pagination controls to navigate between pages. The number of items per page adjusts automatically based on screen size.",
    color: "#607D8B",
    position: "top",
    highlight: "Navigate through multiple pages",
  },
  {
    id: "done",
    targetId: null,
    title: "🎉 You're All Set!",
    description:
      "You've completed the Synthium Studio tour! You can now create new projects, upload files, use templates, organize with folders, and manage all your documents like a pro. Happy writing!",
    color: "#4CAF50",
    position: "center",
    highlight: null,
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
//
//  Usage in CoAuthor.jsx:
//    import CoAuthorFeatureBot from "./CoAuthorFeatureBot";
//    // inside the return, before </BackgroundContainer>:
//    <CoAuthorFeatureBot show={true} />
//
// ─────────────────────────────────────────────────────────────────────────────

export default function CoAuthorFeatureBot({ show = true }) {
  const [isOpen,          setIsOpen]          = useState(false);
  const [currentStep,     setCurrentStep]     = useState(0);
  const [isTyping,        setIsTyping]        = useState(false);
  const [displayedText,   setDisplayedText]   = useState("");
  const [targetRect,      setTargetRect]      = useState(null);
  const [cardPos,         setCardPos]         = useState({
    top: "50%", left: "50%", transform: "translate(-50%, -50%)",
  });

  const typingRef = useRef(null);

  const step     = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const findTarget = useCallback((targetId) => {
    if (!targetId) return null;
    return document.getElementById(targetId);
  }, []);

  const positionCard = useCallback((el, position) => {
    if (!el) {
      setTargetRect(null);
      setCardPos({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      const rect  = el.getBoundingClientRect();
      setTargetRect(rect);
      const cardW = 395, cardH = 285, margin = 16;
      const vw    = window.innerWidth, vh = window.innerHeight;
      let top, left;
      switch (position) {
        case "bottom": top = rect.bottom + margin;                     left = rect.left + rect.width / 2 - cardW / 2; break;
        case "top":    top = rect.top - cardH - margin;                left = rect.left + rect.width / 2 - cardW / 2; break;
        case "right":  top = rect.top + rect.height / 2 - cardH / 2;  left = rect.right + margin;                    break;
        case "left":   top = rect.top + rect.height / 2 - cardH / 2;  left = rect.left - cardW - margin;             break;
        default:       top = rect.bottom + margin;                     left = rect.left + rect.width / 2 - cardW / 2;
      }
      left = Math.max(margin, Math.min(left, vw - cardW - margin));
      top  = Math.max(margin, Math.min(top,  vh - cardH - margin));
      setCardPos({ top, left, transform: "none" });
    }, 420);
  }, []);

  const typeText = useCallback((text) => {
    setIsTyping(true);
    setDisplayedText("");
    let i = 0;
    clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      if (i < text.length) { setDisplayedText(text.slice(0, i + 1)); i++; }
      else { clearInterval(typingRef.current); setIsTyping(false); }
    }, 16);
  }, []);

  const goToStep = useCallback((idx) => {
    const s = TOUR_STEPS[idx];
    setCurrentStep(idx);
    const el = findTarget(s.targetId);
    positionCard(el, s.position);
    setTimeout(() => typeText(s.description), 300);
  }, [findTarget, positionCard, typeText]);

  const startTour = () => { setIsOpen(true); goToStep(0); };
  const closeTour = () => { setIsOpen(false); setCurrentStep(0); setTargetRect(null); clearInterval(typingRef.current); };
  const next = () => { if (currentStep < TOUR_STEPS.length - 1) goToStep(currentStep + 1); else closeTour(); };
  const prev = () => { if (currentStep > 0) goToStep(currentStep - 1); };

  useEffect(() => () => clearInterval(typingRef.current), []);

  if (!show) return null;

  return (
    <>
      {/* ── Floating Launcher ── */}
      {!isOpen && (
        <Box sx={{ position: "fixed", bottom: 32, right: 32, zIndex: 1300, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Fade in>
            <Box sx={{ position: "relative" }}>
              <PulseRingBox sx={{ top: "-2px", left: "-2px" }} />
              <BotAvatarStyled onClick={startTour}>
                <StudioIcon sx={{ fontSize: 26 }} />
              </BotAvatarStyled>
            </Box>
          </Fade>
          <Paper
            elevation={4}
            sx={{ px: 2, py: 0.75, borderRadius: 50, background: GRADIENT, backgroundSize: "200% auto", animation: `${shimmer} 3s linear infinite`, cursor: "pointer" }}
            onClick={startTour}
          >
            <Typography variant="caption" fontWeight={700} color="white" noWrap>✨ Studio Tour</Typography>
          </Paper>
        </Box>
      )}

      {/* ── Spotlight Overlay ── */}
      {isOpen && (
        <SpotlightOverlay>
          <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
            <defs>
              <mask id="coauthor-spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                {targetRect && (
                  <rect x={targetRect.left - 8} y={targetRect.top - 8} width={targetRect.width + 16} height={targetRect.height + 16} rx={12} fill="black" />
                )}
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#coauthor-spotlight-mask)" />
            {targetRect && (
              <>
                <rect x={targetRect.left - 8} y={targetRect.top - 8} width={targetRect.width + 16} height={targetRect.height + 16} rx={12} fill="none" stroke={step.color} strokeWidth={3} opacity={0.9} />
                {[
                  [targetRect.left  - 10, targetRect.top    - 10],
                  [targetRect.right - 20, targetRect.top    - 10],
                  [targetRect.left  - 10, targetRect.bottom - 20],
                  [targetRect.right - 20, targetRect.bottom - 20],
                ].map(([cx, cy], i) => (
                  <g key={i} transform={`translate(${cx},${cy})`}>
                    <rect width={20} height={20} rx={4} fill={step.color} opacity={0.25} />
                  </g>
                ))}
              </>
            )}
          </svg>
        </SpotlightOverlay>
      )}

      {/* ── Bot Card ── */}
      {isOpen && (
        <BotCard style={{ top: cardPos.top, left: cardPos.left, transform: cardPos.transform }}>

          {/* Progress */}
          <ProgressBar variant="determinate" value={progress} />

          {/* Header */}
          <Box sx={{ background: `linear-gradient(135deg, ${step.color}dd, ${step.color}99)`, px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ width: 38, height: 38, background: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.4)", animation: `${floatBot} 3s ease-in-out infinite` }}>
              <StudioIcon sx={{ fontSize: 20, color: "white" }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", fontSize: 10 }}>
                Synthium Guide Bot
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} color="white" lineHeight={1.2}>
                {step.title}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={`${currentStep + 1}/${TOUR_STEPS.length}`}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 700, fontSize: 10, height: 22 }}
              />
              <IconButton size="small" onClick={closeTour} sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,68,68,0.6)" }, width: 28, height: 28 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ px: 2.5, py: 2, bgcolor: "white", minHeight: 90 }}>
            {step.highlight && (
              <Chip
                label={step.highlight}
                size="small"
                sx={{ mb: 1.5, bgcolor: alpha(step.color, 0.1), color: step.color, fontWeight: 600, fontSize: 11, border: `1px solid ${alpha(step.color, 0.3)}` }}
              />
            )}
            <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
              {displayedText}
              {isTyping && (
                <Box component="span" sx={{ display: "inline-block", ml: 0.5 }}>
                  <TypingIndicator />
                </Box>
              )}
            </Typography>
          </Box>

          {/* Footer */}
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#f4f6ff", borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              {currentStep > 0 && (
                <IconButton size="small" onClick={prev} sx={{ border: `1px solid ${alpha(PRIMARY, 0.3)}`, color: PRIMARY, "&:hover": { bgcolor: alpha(PRIMARY, 0.08) }, width: 32, height: 32 }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              <Button size="small" onClick={closeTour} sx={{ textTransform: "none", color: "text.secondary", fontSize: 12, "&:hover": { color: "error.main" } }}>
                Skip tour
              </Button>
            </Box>
            <NextButton size="small" onClick={next} endIcon={<ArrowForwardIcon />}>
              {currentStep === TOUR_STEPS.length - 1 ? "Finish 🎉" : "Next"}
            </NextButton>
          </Box>
        </BotCard>
      )}
    </>
  );
}